import { lazy, Suspense, useState, useEffect, Fragment } from 'react';

import { useTranslator } from '@picsart/rc/services/localization';
import Input from '@/components/Input';
import MediaInputsGrid from '@/components/MediaInputsGrid';
import CanvasImageSelector from '@/components/CanvasImageSelector';
import { useAppStore, type IAppStore } from '@/store';
import useGenerate from '@/hooks/useGenerate';
import { useMediaHandlers } from '@/hooks/useMediaHandlers';
import useCanvasImages from '@/hooks/useCanvasImages';
import { InputType } from '@/types';
import getRemoteSettings from '@/utils/user/getRemoteSettings';
import isNativeMobile from '@/utils/platform/isNativeMobile';
import isWindows from '@/utils/platform/isWindows';
import { isPortfolioApp as checkIsPortfolioApp } from '@/utils/environment/surface';
import useStyles from './styles';
import type { ITextInput, IImageInput, IVideoInput } from '@/types';
import type { InputsSectionProps } from './types';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { AiGenScreenAction, ScreenType } from '@/types/analytics';

const SuggestionChips = lazy(() => import('@/components/SuggestionChips'));

const DEFAULT_TEXT_INPUT_TITLE = 'Enter text';
let cachedTextInputTitle: string | null = null;

const selector = (store: IAppStore) => ({
  selectedPreset: store.selectedPreset,
  inputs: store.inputs,
});

/**
 * Renders text + media inputs for the current preset. Owns the media
 * handlers (upload, remove, drive picker, native picker) and the canvas
 * image flow.
 */
const InputsSection = ({ uploadSectionRef }: InputsSectionProps) => {
  const classes = useStyles();
  const translate = useTranslator();
  const { selectedPreset, inputs } = useAppStore(selector);
  const nativeMobile = isNativeMobile();

  const isPortfolioApp = checkIsPortfolioApp();
  const deviceUploadOnly = isPortfolioApp && isWindows();

  const { uploadMedia } = useGenerate();
  const handlers = useMediaHandlers({ uploadMedia });
  const canvas = useCanvasImages({ flowId: selectedPreset?.flowId });

  const [textInputTitle, setTextInputTitle] = useState(
    cachedTextInputTitle || DEFAULT_TEXT_INPUT_TITLE
  );

  useEffect(() => {
    if (cachedTextInputTitle) return;
    getRemoteSettings<string>({
      name: 'gen_ai_text_input_title',
      tag: 'gen_ai_text_input_title',
      defaultValue: DEFAULT_TEXT_INPUT_TITLE,
      validate: (s) => typeof s === 'string' && s.length > 0,
    }).then((title) => {
      cachedTextInputTitle = title;
      setTextInputTitle(title);
    });
  }, []);

  const textInputs = inputs.filter(
    (input): input is ITextInput => input.type === InputType.TEXT
  );

  const mediaInputs = inputs.filter(
    (input): input is IImageInput | IVideoInput => input.type !== InputType.TEXT
  );

  const onSelect = (id: string, value: string) => {
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.CHIP_CLICK,
      screen_type: ScreenType.GENERATOR,
    });
    handlers.handleTextChange(id, value);
  };

  return (
    <>
      <div ref={uploadSectionRef} className={classes.uploadSection}>
        {textInputs.map((input) => (
          <Fragment key={input.id}>
            {!!input.suggestions?.length && (
              <Suspense fallback={null}>
                <SuggestionChips
                  suggestions={input.suggestions ?? []}
                  value={input.value || ''}
                  onSelect={(value) => onSelect(input.id, value)}
                />
              </Suspense>
            )}
            <Input
              label={translate(input.title || textInputTitle)}
              placeholder={translate(
                input.placeholder ?? 'Describe what you want to create...'
              )}
              value={input.value || ''}
              onChange={(value) => handlers.handleTextChange(input.id, value)}
            />
          </Fragment>
        ))}

        <MediaInputsGrid
          mediaInputs={mediaInputs}
          handlers={handlers}
          canvas={canvas}
          nativeMobile={nativeMobile}
          deviceUploadOnly={deviceUploadOnly}
        />
      </div>
      <CanvasImageSelector
        isOpen={canvas.isOpen}
        images={canvas.images}
        onSelect={canvas.handleSelect}
        onClose={canvas.closeSelector}
      />
    </>
  );
};

export default InputsSection;
